/* ---------------------------------------------------------------------------
   What was modelled here, remembered on the device.

   WHY THIS EXISTS, AND IT IS NOT ABOUT SPEED. src/core/geometryCache.ts already
   keeps the `.glb` on the device, so the record's 3D view can paint in the frame
   it opens in. It could not, and the reason was this: the bytes are keyed by
   `bridgeId/modelId`, and NOTHING ON THE DEVICE KNEW THE MODEL ID. That comes
   off the model index — one GET /api/modeler/models — and until it lands
   `heroModel` returns null, so src/modeler/meshView.ts has nothing to mount and
   the record says "No 3D model built" over a structure whose geometry is sitting
   in IndexedDB three inches from the screen.

   That read is also the slowest one the record makes. functions/api/modeler/models.ts
   documents it: six round trips, measured at 6.8 seconds against remote bindings,
   against the 12-second budget in ./api.ts. Miss the budget — a bad moment on the
   connection, or the six other reads a record opens with contending for it — and
   the view was a coin toss on a structure that had been drawn correctly a minute
   earlier.

   So the index is remembered, and the shape of the answer follows the one
   ./geometryCache.ts already established: THE DEVICE ANSWERS FIRST, THE SERVER
   ANSWERS SECOND. A record opens against what this device last heard, the read
   goes out anyway, and its answer replaces this the moment it arrives. A read
   that fails changes nothing — which is the whole point, and is the rule
   `modelsRead` was added to the store to express.

   WHAT IT COSTS TO BE WRONG, stated plainly because a cache that cannot be
   wrong is not doing anything. The index can name a model somebody has since
   deleted. The record then draws it until the read lands, and the store marks
   the state remembered so the model card can say so. The geometry behind it is
   subject to the same conditional check it always was — ./geometryCache.ts drops
   its copy on a 404 — so a deleted model's bytes do not survive a successful
   round trip either. Set against that: the view that used to vanish was drawing
   CURRENT geometry, verified by ETag, and it vanished because a metadata request
   was slow.

   LOCALSTORAGE, NOT INDEXEDDB, and the two are not a matter of taste here. The
   payload is a few hundred bytes per structure — names, flags and timestamps —
   where a mesh is megabytes. Reading it is synchronous, which means `selectInto`
   can seed the index in the SAME patch that moves the selection rather than in a
   later one, and the record paints once instead of twice. And it is a different
   database from the geometry store's, so nothing here can make that one's open
   fail; src/core/geometryCache.ts explains why that matters.

   NOTHING HERE THROWS. Private mode refuses the write, a full profile refuses
   the quota, a payload from an older build will not parse. Every one of those
   means "this device remembers nothing", which is exactly the behaviour the
   record had before this file existed.
   --------------------------------------------------------------------------- */

import type { ModelIndex, ModelSummary } from '../data/types.ts';

/**
 * Its own key, beside the store's `bridge-monitor/v1` rather than inside it.
 *
 * The store's payload is pruned on load against register data the page may not
 * have fetched (see `load` in ./store.ts), and this is per-structure data for
 * structures that are not the selected one — it would be pruned away or would
 * have to teach the pruner about itself. The `v1` is doing real work: a change
 * to `ModelSummary` that this file's reader would accept but misread is a reason
 * to bump it, and an unreadable key is simply an empty cache.
 */
const STORAGE_KEY = 'bridge-watch/model-index/v1';

/**
 * How many structures to remember.
 *
 * An engineer works a corridor and comes back to the same handful for weeks, so
 * this wants to be larger than one; it is a few hundred bytes each, so it can
 * afford to be. The least recently WRITTEN goes, not the least recently read: a
 * successful read rewrites its entry, so writing is the only signal there is and
 * tracking a second one would mean rewriting the payload on every record opened.
 */
export const KEEP_BRIDGES = 24;

interface Remembered {
  index: ModelIndex;
  /** When this was written. The eviction order — see `stamp`. */
  savedAt: number;
}

/** The last stamp handed out, so the next one is never the same or smaller. */
let lastStamp = 0;

/**
 * A monotonic "when this was written", exactly as ./geometryCache.ts does it.
 *
 * `Date.now()` alone is wrong here for the reason that only shows up in the case
 * eviction exists for: opening a corridor of structures one after another writes
 * several entries inside the same millisecond, they all carry the same stamp,
 * and the sort has no order to work with — so the victim is whichever the
 * payload happened to list first, which can be the one just written. Bumping
 * past the clock when it has not moved keeps the order strictly by write while
 * leaving the value a wall-clock time, which is what makes stamps from an
 * earlier session and this one comparable at all.
 */
function stamp(): number {
  lastStamp = Math.max(Date.now(), lastStamp + 1);
  return lastStamp;
}

type Payload = Record<string, Remembered>;

/* --------------------------- reading it back --------------------------- */

function str(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function bool(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null;
}

/**
 * One model, field by field, or null if anything is not what it says it is.
 *
 * BUILT EXPLICITLY RATHER THAN CAST, and that is the point of the tedium. The
 * object handed back here reaches `heroModel`, the record's model card and
 * meshView's mount test, which between them read every field on it — so a
 * payload written by an older build must not arrive with a field missing and a
 * screen reading `undefined` off it. Listing them also means that ADDING a field
 * to `ModelSummary` fails this file at the typecheck rather than in front of
 * somebody, which is the loud failure a `JSON.parse as ModelSummary` could never
 * give.
 */
function readModel(value: unknown): ModelSummary | null {
  if (typeof value !== 'object' || value === null) return null;
  const raw = value as Record<string, unknown>;

  const id = str(raw['id']);
  const name = str(raw['name']);
  const templateId = str(raw['templateId']);
  const updatedAt = raw['updatedAt'];
  const sizeBytes = raw['sizeBytes'];
  const hasThumb = bool(raw['hasThumb']);
  const hasGeometry = bool(raw['hasGeometry']);
  const hasSource = bool(raw['hasSource']);
  const geometryState = str(raw['geometryState']);
  const imported = raw['imported'];
  const geometryVersion = raw['geometryVersion'];

  if (id === null || id === '' || name === null || templateId === null) return null;
  if (typeof updatedAt !== 'number' || !Number.isFinite(updatedAt)) return null;
  if (sizeBytes !== null && typeof sizeBytes !== 'number') return null;
  if (hasThumb === null || hasGeometry === null || hasSource === null) return null;
  if (
    geometryState !== 'none' &&
    geometryState !== 'fresh' &&
    geometryState !== 'stale' &&
    geometryState !== 'unknown'
  ) {
    return null;
  }
  if (imported !== null && typeof imported !== 'boolean') return null;
  /* Absent rather than null is a payload from a build before this field
     existed. Refused with the rest of the entry: the geometry URL and the
     cache's freshness test are both keyed on it, and a model that reads back
     with it missing would quietly go back to a round trip per mount. */
  if (geometryVersion !== null && typeof geometryVersion !== 'string') return null;

  return {
    id,
    name,
    templateId,
    updatedAt,
    sizeBytes,
    hasThumb,
    hasGeometry,
    hasSource,
    geometryState,
    imported,
    geometryVersion,
  };
}

/** One structure's index, or null where any part of it is unreadable. */
function readIndex(value: unknown): ModelIndex | null {
  if (typeof value !== 'object' || value === null) return null;
  const raw = value as Record<string, unknown>;

  const models = raw['models'];
  if (!Array.isArray(models)) return null;
  const lastSavedAt = raw['lastSavedAt'];
  if (lastSavedAt !== null && typeof lastSavedAt !== 'string') return null;
  const totalBytes = raw['totalBytes'];
  if (typeof totalBytes !== 'number' || !Number.isFinite(totalBytes)) return null;

  const read: ModelSummary[] = [];
  for (const model of models) {
    const one = readModel(model);
    /* ALL OR NOTHING. A partly-read index is an index that has silently lost a
       model, and the record would then state — from the device, with no read in
       flight — that a structure has one model when it has two. */
    if (!one) return null;
    read.push(one);
  }

  return { models: read, lastSavedAt, totalBytes };
}

function load(): Payload {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};

    const out: Payload = {};
    for (const [bridgeId, entry] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof entry !== 'object' || entry === null) continue;
      const savedAt = (entry as Record<string, unknown>)['savedAt'];
      const index = readIndex((entry as Record<string, unknown>)['index']);
      /* One unreadable structure is dropped and the rest are kept: a build that
         changed the shape of a model should cost the structures it was written
         for, not every structure on the device. */
      if (!index || typeof savedAt !== 'number' || !Number.isFinite(savedAt)) continue;
      out[bridgeId] = { index, savedAt };
    }
    return out;
  } catch {
    /* Private mode, storage disabled, or a payload this build cannot parse. */
    return {};
  }
}

function save(payload: Payload): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* A refused quota or a profile that will not store. The record has the index
       it just read either way; all that is lost is the head start next time. */
  }
}

/* --------------------------- the front door --------------------------- */

/**
 * What this device last heard about a structure's models, or null.
 *
 * Synchronous, and that is load-bearing: `selectInto` seeds the store with this
 * in the same patch that moves the selection, so the record paints once.
 */
export function recallModelIndex(bridgeId: string): ModelIndex | null {
  if (!bridgeId) return null;
  return load()[bridgeId]?.index ?? null;
}

/**
 * Record what the register just said, and drop the oldest if that is one too many.
 *
 * Called only on a SUCCESSFUL read. A failed one must not touch this — what is
 * stored is the last thing the register actually said, and overwriting it with
 * an empty index on a timeout is precisely the bug `modelsRead` exists to
 * prevent, moved onto the disk where it would outlive the page.
 */
export function rememberModelIndex(bridgeId: string, index: ModelIndex): void {
  if (!bridgeId) return;
  const payload = load();
  payload[bridgeId] = { index, savedAt: stamp() };

  const ids = Object.keys(payload);
  if (ids.length > KEEP_BRIDGES) {
    ids
      .sort((a, b) => (payload[b]?.savedAt ?? 0) - (payload[a]?.savedAt ?? 0))
      .slice(KEEP_BRIDGES)
      .forEach((id) => delete payload[id]);
  }

  save(payload);
}

/**
 * Forget everything. For the tests, and for a future "reset this device".
 *
 * There is deliberately no forget-one: every way a model can stop existing goes
 * through `refreshModels`, which reads the index the delete left behind and
 * remembers THAT. A second path for removing an entry would be a second thing to
 * keep in step with the register, and it would be the one nobody calls.
 */
export function forgetAllModelIndexes(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* Nothing stored, or a store that will not. Either way there is nothing to lose. */
  }
}
